import { Box, Button, CircularProgress, Grid, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { usePayPackageStyles } from './PayPackage.styles';
import ScenarioTreeView from './ScenarioNavigationTree/ScenarioTreeView';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectPlacementMarginToolDetailsData,
  selectPlacementMarginOrderIdModifiedConfirmationStatus,
  selectSelectedPlacementMarginScenario,
  selectPlacementMarginTreeViewSelectedBookingPeriod,
} from 'store/redux-store/margin-tool/slices/placement/placement-add-edit-scenario/add-edit-placement-scenario.selector';
import { PlacementMarginPayPackageStatus } from 'store/redux-store/margin-tool/slices/placement/placement-pay-package-status/placement-pay-package-status.model';
import { getAddScenarioButtonState } from './helper';
import { PayPackageOptions } from '@AMIEWEB/MarginTool/enum';
import { CustomTooltip } from 'app/components/Common/Tooltips';
import { PayPackagePIDSearch } from './PayPackagePIDSearch';
import { useParams } from 'react-router-dom';
import { MarginEntityType } from 'store/redux-store/margin-tool/slices/placement/placement-add-edit-scenario/add-edit-placement-scenario.model';
import { usePromiseTracker } from 'react-promise-tracker';
import { PromiseTrackerKeys } from 'app/constants/PromiseTrackerKeys';
import { placementMarginScenarioActions } from 'store/redux-store/margin-tool/slices/placement/placement-add-edit-scenario/add-edit-placement-scenario.redux';
import { findMarginDetailByBookingPeriodId } from './AddScenario/helper';
import { globalActions } from 'app/ApplicationRoot/Global.redux';
import { useFormContext } from 'react-hook-form';
import { useReadOnly } from 'oidc/userRoles';

export const PayPackages = () => {
  const { classes } = usePayPackageStyles();
  const params = useParams<{ entity: string; entityId: string }>();
  const treeViewData = useSelector(selectPlacementMarginToolDetailsData);
  const treeViewSelectedBookingPeriod = useSelector(selectPlacementMarginTreeViewSelectedBookingPeriod);
  const orderIdModifiedConfirmationStatus = useSelector(selectPlacementMarginOrderIdModifiedConfirmationStatus);
  const [hasUnVerifiedStatus, setHasUnVerifiedStatus] = useState(true);
  const [buttonDisabledStateAndMessage, setButtonDisabledStateAndMessage] = useState({ state: false, message: '' });
  const marginToolDetails = useSelector(selectPlacementMarginToolDetailsData);
  const selectedScenario = useSelector(selectSelectedPlacementMarginScenario);
  const { readOnly}  = useReadOnly();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  //const [, setIsModalOpen] = useContext(ModalStatusContext);

  const { promiseInProgress: flagVariationInProgress } = usePromiseTracker({
    area: PromiseTrackerKeys.placementMarginTool.peopleSoftCallMarginTool,
    delay: 0,
  });
  const { promiseInProgress: isSavingScenario } = usePromiseTracker({
    area: PromiseTrackerKeys.placementMarginTool.saveScenario,
    delay: 0,
  });
  const { promiseInProgress: isLoadDataScenario } = usePromiseTracker({
    area: PromiseTrackerKeys.placementMarginTool.scenarioNavigationData,
    delay: 0,
  });

  const { promiseInProgress: isScenarioVerification } = usePromiseTracker({
    area: PromiseTrackerKeys.placementMarginTool.validateScenario,
    delay: 0,
  });

  const { promiseInProgress: isAddScenarioDetailsFetchData } = usePromiseTracker({
    area: PromiseTrackerKeys.placementMarginTool.getAddScenarioDetailsFetchData,
    delay: 0,
  });

  const { promiseInProgress: isAddScenarioExtensionDetailsFetchData } = usePromiseTracker({
    area: PromiseTrackerKeys.placementMarginTool.getAddScenarioExtensionDetailsFetchData,
    delay: 0,
  });

  const { promiseInProgress: isGSACalculateFetchDataInProgress } = usePromiseTracker({
    area: PromiseTrackerKeys.placementMarginTool.gsaCalculateFetchData,
    delay: 0,
  });

  const { promiseInProgress: isPeopleSoftCallMarginToolInProgress } = usePromiseTracker({
    area: PromiseTrackerKeys.placementMarginTool.peopleSoftCallMarginToolProgress,
    delay: 0,
  });

  const { promiseInProgress: isCalculatedUpdateInProgress } = usePromiseTracker({
    area: PromiseTrackerKeys.placementMarginTool.updateScnarioCalculatedValues,
    delay: 0,
  });

  const inProgress =
    flagVariationInProgress ||
    isSavingScenario ||
    isLoadDataScenario ||
    isScenarioVerification ||
    isAddScenarioDetailsFetchData ||
    isAddScenarioExtensionDetailsFetchData ||
    isGSACalculateFetchDataInProgress ||
    isPeopleSoftCallMarginToolInProgress ||
    isCalculatedUpdateInProgress;

  useEffect(() => {
    const selectedBookingPeriodTemp =
      treeViewData?.length > 0 && treeViewSelectedBookingPeriod?.bookingPeriodId > 0
        ? treeViewData?.find(x => x.bookingPeriodId === treeViewSelectedBookingPeriod?.bookingPeriodId)
        : null;
    const placementIdParam =
      params.entityId && params.entity === MarginEntityType.Placement ? Number(params.entityId) : null;
    setButtonDisabledStateAndMessage(
      getAddScenarioButtonState(placementIdParam, treeViewSelectedBookingPeriod, selectedBookingPeriodTemp, t),
    );
  }, [treeViewSelectedBookingPeriod, treeViewData, t, params.entityId, params.entity]);

  useEffect(() => {
    if (treeViewData && treeViewData.length > 0) {
      const scenarios = (
        treeViewData?.length > 0 && treeViewSelectedBookingPeriod?.bookingPeriodId > 0
          ? treeViewData?.find(x => x.bookingPeriodId === treeViewSelectedBookingPeriod?.bookingPeriodId)
          : null
      )?.scenarios;
      if (scenarios && scenarios.length > 0) {
        const unVerifiedStatusList = scenarios?.filter(
          x =>
            x.scenarioStatusId === null ||
            x.scenarioStatusId === undefined ||
            x.scenarioStatusId !== PlacementMarginPayPackageStatus.Verified,
        );
        const unVerifiedStatus = unVerifiedStatusList.length > 0 ? true : false;
        setHasUnVerifiedStatus(unVerifiedStatus);
      } else {
        setHasUnVerifiedStatus(true);
      }
    } else {
      setHasUnVerifiedStatus(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [treeViewData, treeViewSelectedBookingPeriod]);

  const openScenarioModal = modalName => {
    //setIsModalOpen(true, modalName);
    if (treeViewSelectedBookingPeriod?.placementId || (params?.entityId && params?.entity === 'placement')) {
      const placementId =
        treeViewSelectedBookingPeriod?.placementId ??
        Number(selectedScenario?.placementId ? selectedScenario?.placementId : params?.entityId);
      if (modalName === PayPackageOptions.AddScenario) {
        let extFlow = false;
        if (marginToolDetails?.length > 0 && treeViewSelectedBookingPeriod?.bookingPeriodId > 0) {
          const matchedItem = findMarginDetailByBookingPeriodId(
            marginToolDetails,
            treeViewSelectedBookingPeriod.bookingPeriodId,
          );
          extFlow = matchedItem ? matchedItem.isScenarioExtention : false;
        }
        dispatch(
          placementMarginScenarioActions.getAddPlacementMarginScenarioDetails({
            placementId: treeViewSelectedBookingPeriod.placementId ?? Number(params?.entityId),
            bookingPeriodId: treeViewSelectedBookingPeriod?.bookingPeriodId,
            isExtensionFlow: extFlow,
          }),
        );
      } else if (modalName === PayPackageOptions.AddExtension) {
        dispatch(placementMarginScenarioActions.getAddPlacementMarginExtensionDetails({ placementId: placementId }));
      }
    }
    dispatch(placementMarginScenarioActions.setPlacementMarginScenarioCreateUpdateResponse(null));
    dispatch(globalActions.closeBanner());
  };

  return (
    <>
      <Grid container item className={classes.formStyle} id="marginTool-marginToolDetailsPage-payPackagesContainer">
        <Grid item classes={{ root: classes.headerContainer }}>
          <Typography sx={{ fontWeight: 'bold' }} variant="subtitle1">
            {t('marginTool.components.payPackages')}
          </Typography>
        </Grid>
        <Grid container item classes={{ root: classes.contentContainer }}>
          <PayPackagePIDSearch />
        </Grid>
        <Grid container item className={classes.buttonGrid} spacing={2}>
          <Grid item>
            <CustomTooltip tooltipContent={buttonDisabledStateAndMessage.message}>
              <Button
                className={classes.button}
                onClick={() => {
                  if (orderIdModifiedConfirmationStatus) {
                    dispatch(placementMarginScenarioActions.setPlacementMarginShowOrderIdModifiedDialogStatus(true));
                  } else {
                    openScenarioModal(PayPackageOptions.AddScenario);
                  }
                }}
                disabled={buttonDisabledStateAndMessage.state || inProgress || readOnly}
                variant="contained"
                color="primary"
                id="marginTool-marginToolDetailsPage-addScenario-button"
              >
                {t('marginTool.buttons.addScenario')}
                {(isAddScenarioDetailsFetchData ||
                  isGSACalculateFetchDataInProgress ||
                  isPeopleSoftCallMarginToolInProgress ||
                  isCalculatedUpdateInProgress) && (
                  <Box position={'absolute'} right={1} top={6}>
                    <CircularProgress size={12} color="inherit" />
                  </Box>
                )}
              </Button>
            </CustomTooltip>
          </Grid>
          <Grid item>
            <CustomTooltip
              standardMargin
              tooltipContent={hasUnVerifiedStatus ? t('marginTool.components.disabledduetoScenarioStatus') : ''}
            >
              <Button
                className={classes.button}
                onClick={() => {
                  if (orderIdModifiedConfirmationStatus) {
                    dispatch(placementMarginScenarioActions.setPlacementMarginShowOrderIdModifiedDialogStatus(true));
                  } else {
                    openScenarioModal(PayPackageOptions.AddExtension);
                  }
                }}
                disabled={hasUnVerifiedStatus || inProgress || readOnly}
                variant="contained"
                color="primary"
                id="marginTool-marginToolDetailsPage-addExtension-button"
              >
                {t('marginTool.buttons.addExtension')}
                {isAddScenarioExtensionDetailsFetchData && (
                  <Box position={'absolute'} right={1} top={6}>
                    <CircularProgress size={12} color="inherit" />
                  </Box>
                )}
              </Button>
            </CustomTooltip>
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <ScenarioTreeView />
        </Grid>
      </Grid>
    </>
  );
};
